import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Lava from "./Lava";

// Build-time env config — Vite's `define` replaces these constants with
// per-env values during the build. One picker bundle per env is deployed to
// gs://raindroplogin/<env>/lookup/ and served at <env>login.raindrop.com.
//
// Defaults here cover local `npm run dev` so the lookup is usable without
// running the full per-env build pipeline.
declare const __ENV__: "dev" | "stg" | "us1";
declare const __AUTH0_HOST__: string;
declare const __LOOKUP_URL__: string;
declare const __MCP_CLIENT_ID__: string;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INSTANCE_HISTORY_KEY = "loginInstanceHistory";
const INSTANCE_HISTORY_MAX = 5;
const LAST_EMAIL_KEY = "loginLastEmail";

// The MCP login client — Auth0's "mcp" native app, used when a user signs in
// through the Claude.ai / ChatGPT MCP connectors. It's a fixed, statically
// registered app (not Dynamic Client Registration), so its client_id arrives
// verbatim in the /authorize params and is stable per tenant. The id is baked
// in per-env at build time from the MCP_CLIENT_ID pipeline variable (see
// vite.config.ts); empty for local/dev builds that don't set it, which simply
// disables the MCP special-casing below.
const MCP_CLIENT_ID = __MCP_CLIENT_ID__;
// Instance hidden from MCP logins; dropping it lets the single-instance
// auto-login take over when it was the only other choice.
const MCP_HIDDEN_INSTANCE_ID = 500;

function readLastEmail(): string {
  try {
    return window.localStorage.getItem(LAST_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeLastEmail(email: string) {
  try {
    window.localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    // localStorage unavailable; silently skip
  }
}

interface InstanceConnections {
  id: number;
  connections: string[];
}

interface LookupResponse {
  state: string;
  connections: string[];
  instances: InstanceConnections[];
}

function readHistory(): number[] {
  try {
    const raw = window.localStorage.getItem(INSTANCE_HISTORY_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as { instance: number }[]).map((e) => e.instance);
  } catch {
    return [];
  }
}

function pushHistory(instance: number) {
  try {
    const raw = window.localStorage.getItem(INSTANCE_HISTORY_KEY);
    const arr: { instance: number }[] = raw ? JSON.parse(raw) : [];
    const deduped = arr.filter((e) => e.instance !== instance);
    deduped.push({ instance });
    const trimmed = deduped.slice(-INSTANCE_HISTORY_MAX);
    window.localStorage.setItem(INSTANCE_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage unavailable; silently skip
  }
}

// Build the /authorize URL the picker will eventually redirect to. The caller
// navigates directly to https://<env>login.raindrop.com/?<authorize-params>
// — i.e. exactly the params they'd have passed to /authorize, just with the
// lookup domain in front. We forward those params verbatim to the env's
// Auth0 host.
//
// When the user clicks "Use a different email" on an ACUL screen we bounce
// them back to this page with `?return=<nonce>`. The original authorize
// params were stashed in localStorage under that nonce when we first
// redirected to /authorize; we restore them here and rewrite the URL in
// place so the rest of the lookup flow sees them as if the user arrived
// fresh from the calling app.
const RETURN_NONCE_TTL_MS = 15 * 60 * 1000;
const RETURN_NONCE_PREFIX = "lookupReturn:";

function pruneOldReturnEntries() {
  try {
    const cutoff = Date.now() - RETURN_NONCE_TTL_MS;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(RETURN_NONCE_PREFIX)) continue;
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) continue;
        const { ts } = JSON.parse(raw);
        if (typeof ts !== "number" || ts < cutoff) {
          window.localStorage.removeItem(key);
        }
      } catch {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable; skip
  }
}

function tryRestoreFromReturnNonce() {
  if (typeof window === "undefined") return;
  const search = new URLSearchParams(window.location.search);
  const nonce = search.get("return");
  if (!nonce) return;
  try {
    const raw = window.localStorage.getItem(RETURN_NONCE_PREFIX + nonce);
    if (!raw) return;
    const { params, ts } = JSON.parse(raw) as {
      params: string;
      ts: number;
    };
    if (typeof params !== "string" || typeof ts !== "number") return;
    if (Date.now() - ts > RETURN_NONCE_TTL_MS) return;
    window.localStorage.removeItem(RETURN_NONCE_PREFIX + nonce);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params}`,
    );
  } catch {
    // ignore — fall through to the normal "no params" early-return path
  }
}

function getAuthorizeParams(): URLSearchParams | null {
  if (typeof window === "undefined") return null;
  tryRestoreFromReturnNonce();
  pruneOldReturnEntries();
  const params = new URLSearchParams(window.location.search);
  if (!params.get("client_id") || !params.get("redirect_uri")) return null;
  return params;
}

// Auth0 tenant-level error page redirects send users here with
// ?error=...&error_description=...&tracking=... when something fails
// before/during the login flow (expired state, invalid request, etc.).
// Detect at any URL — the dedicated /error path is the canonical one but
// Auth0 sometimes appends params to a base URL.
interface ErrorContext {
  code: string;
  description: string;
  tracking: string | null;
}
function getErrorContext(): ErrorContext | null {
  if (typeof window === "undefined") return null;
  // Treat /error as the canonical error route, or any URL with ?error=.
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("error");
  if (!path.startsWith("/error") && !code) return null;
  return {
    code: code ?? "unknown_error",
    description:
      params.get("error_description") ??
      "We couldn't complete your sign-in. Please try again.",
    tracking: params.get("tracking") ?? params.get("trace_id"),
  };
}

// Stable hash → hue shift for the per-email theme tint. Tiny djb2, mapped to
// a narrow ±25° band so the resulting palette stays in the blue/purple range
// (the base lava is cyan/violet — bigger shifts would push it green or red).
function hashHueShift(email: string): number {
  let h = 5381;
  for (let i = 0; i < email.length; i++) h = (h * 33) ^ email.charCodeAt(i);
  return ((h >>> 0) % 51) - 25; // -25 .. +25 deg
}

function RaindropLogo() {
  return (
    <svg
      className="lookup-logo"
      viewBox="0 0 532 149.6"
      role="img"
      aria-label="Raindrop"
    >
      <g fill="currentColor">
        <path d="M 13.7,105.8 H 0 V 69.1 C 0,57.6 5.2,49 14.2,43.7 A 36.5,36.5 0 0 1 49.7,43.5 28.7,28.7 0 0 1 64.2,69 H 50.7 C 50.7,61.3 46.5,57.3 43,55.3 a 23,23 0 0 0 -21.9,0 c -5,3 -7.5,7.5 -7.4,13.7 v 0 z" />
        <path d="m 116.3,41.5 a 34.1,34.1 0 0 0 -13,-2.4 33.1,33.1 0 0 0 -33.2,33.1 c 0,18.3 14.9,33.6 33.2,33.6 a 33,33 0 0 0 21.1,-8 v 8 h 12.1 V 72.2 A 33.3,33.3 0 0 0 116.3,41.5 Z m -13,51.8 a 21.1,21.1 0 1 1 0,-42.2 20.7,20.7 0 0 1 8.2,1.6 21.1,21.1 0 0 1 -8.2,40.6 z" />
        <path d="M 180,105.8 H 166.5 V 69.1 c 0,-11.5 5,-20.1 14.1,-25.4 A 36.3,36.3 0 0 1 216,43.5 c 9.3,5.3 14.6,13.5 14.6,25.5 v 36.8 H 217 V 69 a 16.4,16.4 0 0 0 -7.7,-13.7 23,23 0 0 0 -21.9,0 c -5,3 -7.4,7.4 -7.4,13.7 v 0 z" />
        <path d="m 291.2,0 v 46.6 a 33.2,33.2 0 1 0 -21.1,58.9 32.8,32.8 0 0 0 21.1,-7.6 v 7.9 h 12.1 V 0 Z m -21.1,93.3 a 21.1,21.1 0 1 1 21.1,-21 21.1,21.1 0 0 1 -21.1,21 z" />
        <path d="M 325.8,105.8 H 312.2 V 69.3 c -0.1,-10.9 5.1,-20.1 14.1,-25.4 a 36.2,36.2 0 0 1 35.5,-0.1 c 9.2,5.2 14.5,14.5 14.5,25.4 h -13.5 c 0,-7.7 -4.2,-11.7 -7.7,-13.7 a 23.3,23.3 0 0 0 -21.9,0 c -5,3 -7.5,7.6 -7.4,13.7 v 0 z" />
        <path d="m 488.5,39.1 a 33.2,33.2 0 0 0 -33.2,33.2 v 0 77.2 h 12.1 V 98 a 33.2,33.2 0 1 0 21.1,-58.9 z m 0,54.3 a 21.1,21.1 0 1 1 21.1,-21.1 21.2,21.2 0 0 1 -21.1,21.1 z" />
        <rect height="60.8" width="12.9" y="45" x="146.1" />
        <circle r="9.8" cy="28.3" cx="152.6" />
        <path d="M 415.6,39.1 A 33.4,33.4 0 1 0 449,72.4 33.4,33.4 0 0 0 415.6,39.1 Z m 16,47.2 c -4.8,6.9 -10.7,8.3 -16,8.3 v 0 c -5.2,0 -11.1,-1.4 -16,-8.3 a 14.7,14.7 0 0 1 -0.5,-15.6 c 6.5,-10.6 11.5,-14.4 16.5,-20.5 5,6.1 10,9.9 16.5,20.5 a 14.5,14.5 0 0 1 -0.5,15.6 z" />
      </g>
    </svg>
  );
}

function DotsLoader() {
  return (
    <span className="lookup-dots" aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}

export default function Lookup() {
  const authorizeParams = useMemo(getAuthorizeParams, []);

  const [email, setEmail] = useState(readLastEmail);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    "idle",
  );
  const [result, setResult] = useState<LookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusedInstanceIdx, setFocusedInstanceIdx] = useState(0);

  const allInstances = useMemo(
    () => [...(result?.instances ?? [])].sort((a, b) => a.id - b.id),
    [result],
  );
  const historyOrder = useMemo(() => readHistory().reverse(), [result]);

  // Recent / Other split — computed at top level so all hooks below run
  // unconditionally regardless of the env-error early return path.
  const recentInstances = historyOrder
    .map((id) => allInstances.find((i) => i.id === id))
    .filter((i): i is InstanceConnections => Boolean(i));
  const recentIds = new Set(recentInstances.map((i) => i.id));
  const otherInstances = allInstances.filter((i) => !recentIds.has(i.id));
  const orderedInstances = [...recentInstances, ...otherInstances];

  const showPicker =
    status === "loaded" && allInstances.length > 1 && submittedEmail !== null;

  // Auto-submit when the user stops typing for 600ms with a valid email.
  const lastAutoSubmittedRef = useRef<string | null>(null);
  const runLookupRef = useRef<((emailValue: string) => Promise<void>) | null>(
    null,
  );
  useEffect(() => {
    if (!authorizeParams) return;
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) return;
    if (status === "loading" || status === "loaded") return;
    if (lastAutoSubmittedRef.current === trimmed) return;
    if (submittedEmail) return;
    const handle = setTimeout(() => {
      lastAutoSubmittedRef.current = trimmed;
      setSubmittedEmail(trimmed);
      writeLastEmail(trimmed);
      runLookupRef.current?.(trimmed);
    }, 600);
    return () => clearTimeout(handle);
  }, [email, authorizeParams, status, submittedEmail]);

  const onInstanceKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setFocusedInstanceIdx((idx + 1) % orderedInstances.length);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setFocusedInstanceIdx(
          (idx - 1 + orderedInstances.length) % orderedInstances.length,
        );
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusedInstanceIdx(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusedInstanceIdx(orderedInstances.length - 1);
      }
    },
    [orderedInstances.length],
  );

  useEffect(() => {
    if (!showPicker) return;
    const buttons = document.querySelectorAll<HTMLButtonElement>(
      ".lookup-instance-button",
    );
    buttons[focusedInstanceIdx]?.focus();
  }, [focusedInstanceIdx, showPicker]);

  // Theme hue tint — when a valid email is being submitted, briefly nudge the
  // page's accent hue to a hash of the email so the auth feels personalized.
  useEffect(() => {
    if (!submittedEmail) return;
    document.documentElement.style.setProperty(
      "--theme-hue-shift",
      `${hashHueShift(submittedEmail)}deg`,
    );
  }, [submittedEmail]);

  // Validation progress — 0..1 for how email-like the input is.
  const validationProgress = useMemo(() => {
    const t = email.trim();
    if (!t) return 0;
    if (EMAIL_RE.test(t)) return 1;
    let score = 0;
    if (t.length > 2) score += 0.25;
    if (t.includes("@")) score += 0.35;
    if (/@.+\..+/.test(t)) score += 0.3;
    return Math.min(score, 0.95);
  }, [email]);

  // Auth0 redirects here when something blew up — show a clearly-error
  // variant of the card instead of bouncing the user away.
  const errorCtx = getErrorContext();
  if (errorCtx) {
    const home =
      {
        dev: "https://dev.raindrop.com",
        stg: "https://stg.raindrop.com",
        us1: "https://us1.raindrop.com",
      }[__ENV__] ?? "https://us1.raindrop.com";
    return (
      <>
        <Lava />
        <div className="lookup-shell">
          <div className="lookup-card lookup-card--error">
            <div className="lookup-logo-wrap">
              <RaindropLogo />
            </div>
            <div className="lookup-error-icon" aria-hidden>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="lookup-error-title">Something went wrong</p>
            <p className="lookup-subtitle">{errorCtx.description}</p>
            <a
              className="lookup-button lookup-button--primary"
              href={home}
              style={{ textDecoration: "none", textAlign: "center" }}
            >
              Try again
            </a>
            {errorCtx.tracking && (
              <p className="lookup-error-tracking">
                Reference: <code>{errorCtx.tracking}</code>
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  // No /authorize params on the URL → kick the user back to the env-default
  // app so they're not stranded on an inert page.
  if (!authorizeParams) {
    if (typeof window !== "undefined") {
      const homes: Record<typeof __ENV__, string> = {
        dev: "https://dev.raindrop.com",
        stg: "https://stg.raindrop.com",
        us1: "https://us1.raindrop.com",
      };
      window.location.replace(homes[__ENV__] ?? "https://us1.raindrop.com");
    }
    return null;
  }

  const runLookup = async (emailValue: string) => {
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch(__LOOKUP_URL__, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: emailValue,
          state: crypto.randomUUID(),
        }),
      });
      if (!res.ok) throw new Error(`Lookup failed: ${res.status}`);
      const data = (await res.json()) as LookupResponse;
      // MCP logins must never be offered instance 500. Drop it at the source so
      // both the rendered choice list and the single-instance auto-login below
      // see the filtered set — if 500 was the only other option, auto-login.
      if (MCP_CLIENT_ID && authorizeParams.get("client_id") === MCP_CLIENT_ID) {
        data.instances = data.instances.filter(
          (i) => i.id !== MCP_HIDDEN_INSTANCE_ID,
        );
      }
      setResult(data);
      setStatus("loaded");
      if (data.instances.length === 1) {
        const inst = data.instances[0];
        const conn = inst.connections[0];
        if (conn) {
          redirectWithConnection(inst.id, conn, emailValue);
          return;
        }
      }
      if (data.instances.length === 0) {
        redirectWithConnection(null, null, emailValue);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lookup failed");
      setStatus("error");
    }
  };
  runLookupRef.current = runLookup;

  const onEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmittedEmail(trimmed);
    writeLastEmail(trimmed);
    runLookup(trimmed);
  };

  // Wrap in startViewTransition when available so the email→lookup swap gets
  // a native cross-fade + size morph on Chromium browsers. Falls back to the
  // CSS grid-rows animation everywhere else.
  const withTransition = (fn: () => void) => {
    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    };
    if (typeof doc.startViewTransition === "function") {
      doc.startViewTransition(fn);
    } else {
      fn();
    }
  };

  const redirectWithConnection = (
    instanceId: number | null,
    connection: string | null,
    emailValue: string,
  ) => {
    // Construct the env's /authorize URL using the params the caller passed
    // to this picker page (forwarded verbatim) plus connection / login_hint.
    const params = new URLSearchParams(authorizeParams);

    if (connection) params.set("connection", connection);
    if (emailValue) params.set("login_hint", emailValue);

    // Bake `instance`, `connection`, and `email` into the redirect_uri so
    // they survive the Auth0 round-trip and arrive at the calling app's
    // callback as query params alongside the OAuth code/state.
    const rawRedirect = params.get("redirect_uri");
    if (rawRedirect && (instanceId !== null || connection || emailValue)) {
      try {
        const callback = new URL(rawRedirect);
        if (instanceId !== null) {
          callback.searchParams.set("instance", String(instanceId));
        }
        if (connection) {
          callback.searchParams.set("connection", connection);
        }
        if (emailValue) {
          callback.searchParams.set("email", emailValue);
        }
        params.set("redirect_uri", callback.toString());
      } catch {
        // redirect_uri was malformed — leave it alone rather than failing.
      }
    }

    // Stash the original /authorize params in localStorage keyed by a short
    // random nonce, and pass that nonce through Auth0 as `ext-return_to`.
    // An ACUL screen's "Use a different email" link bounces the user back
    // to this page with `?return=<nonce>`, where tryRestoreFromReturnNonce
    // re-hydrates the URL with the stored params. We keep the nonce short
    // because Auth0's ext-* validation regex caps values at 255 chars.
    if (authorizeParams) {
      try {
        const nonce = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
        const entry = JSON.stringify({
          params: authorizeParams.toString(),
          ts: Date.now(),
        });
        window.localStorage.setItem(RETURN_NONCE_PREFIX + nonce, entry);
        params.set("ext-return_to", nonce);
      } catch {
        // localStorage unavailable — proceed without the return-to hook.
      }
    }

    if (instanceId !== null) pushHistory(instanceId);
    window.location.href = `https://${__AUTH0_HOST__}/authorize?${params}`;
  };

  const handleBack = () => {
    withTransition(() => {
      setSubmittedEmail(null);
      setResult(null);
      setStatus("idle");
      setEmail("");
      setFocusedInstanceIdx(0);
      lastAutoSubmittedRef.current = null;
    });
  };

  return (
    <>
      <Lava />
      <div className="lookup-shell">
        <div
          className="lookup-card"
          data-email-valid={validationProgress === 1}
          style={{ ["--vp" as string]: validationProgress }}
        >
          <div className="lookup-logo-wrap">
            <RaindropLogo />
          </div>
          <p className="lookup-subtitle">
            {showPicker
              ? `Choose an instance for ${submittedEmail}`
              : "Enter your work email"}
          </p>

          {error && <div className="lookup-error">{error}</div>}

          <div
            className={`lookup-collapse ${showPicker ? "is-closed" : "is-open"}`}
          >
            <div className="lookup-collapse__inner">
              <form
                className="lookup-field"
                onSubmit={(e) => {
                  e.preventDefault();
                  withTransition(() => onEmailSubmit(e));
                }}
                autoComplete="off"
              >
                <div className="lookup-input-shell">
                  <input
                    id="email"
                    className="lookup-input"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@company.com"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === "loading"}
                  />
                  <span
                    className="lookup-input-progress"
                    aria-hidden
                    style={{
                      transform: `scaleX(${validationProgress})`,
                    }}
                  />
                  {validationProgress === 1 && (
                    <svg
                      className="lookup-input-check"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <button
                  type="submit"
                  className="lookup-button lookup-button--primary"
                  disabled={
                    status === "loading" || !EMAIL_RE.test(email.trim())
                  }
                  style={{ marginTop: 16 }}
                >
                  {status === "loading" ? <DotsLoader /> : "Continue"}
                </button>
              </form>
            </div>
          </div>

          <div
            className={`lookup-collapse ${showPicker ? "is-open" : "is-closed"}`}
          >
            <div className="lookup-collapse__inner">
              <ul className="lookup-list">
                {recentInstances.length > 0 && (
                  <li>
                    <div className="lookup-section-label">Recent</div>
                  </li>
                )}
                {recentInstances.map((inst, i) => (
                  <li key={inst.id}>
                    <button
                      className="lookup-button lookup-instance-button lookup-instance-button--recent"
                      onClick={() =>
                        redirectWithConnection(
                          inst.id,
                          inst.connections[0],
                          submittedEmail!,
                        )
                      }
                      onKeyDown={(e) => onInstanceKeyDown(e, i)}
                      tabIndex={focusedInstanceIdx === i ? 0 : -1}
                    >
                      Instance {inst.id}
                    </button>
                  </li>
                ))}
                {otherInstances.length > 0 && recentInstances.length > 0 && (
                  <li>
                    <div className="lookup-section-label">Other instances</div>
                  </li>
                )}
                {otherInstances.map((inst, i) => {
                  const idx = recentInstances.length + i;
                  return (
                    <li key={inst.id}>
                      <button
                        className="lookup-button lookup-instance-button"
                        onClick={() =>
                          redirectWithConnection(
                            inst.id,
                            inst.connections[0],
                            submittedEmail!,
                          )
                        }
                        onKeyDown={(e) => onInstanceKeyDown(e, idx)}
                        tabIndex={focusedInstanceIdx === idx ? 0 : -1}
                      >
                        Instance {inst.id}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="lookup-link-row">
                <button
                  type="button"
                  className="lookup-link-button"
                  onClick={handleBack}
                >
                  <svg
                    className="lookup-link-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="15 6 9 12 15 18" />
                  </svg>
                  Back to email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
